/**
 * Tokenising DSL text for display — **not** for parsing.
 *
 * No parser ships to the browser (D11); this classifies characters so they can
 * be coloured, and is deliberately permissive. Half-typed text is the normal
 * state of an editor, so every character must land in some token rather than the
 * tokeniser failing — an editor that stops highlighting when the expression is
 * incomplete is one that stops highlighting exactly when it would help.
 *
 * The token shapes mirror the real lexer in
 * `services/quevee/dsl/expr_parser.py`, including the two decisions a frequency
 * count depends on: an identifier may lead with digits (`2w`, `15mi`) and is
 * matched *before* a number, so `c.2w` does not lex as `.2` then `w`.
 *
 * Knowing the screen's columns is what makes this worth more than pretty
 * colours: an identifier that is not a function, not an alias and not a column
 * on this screen is shown as unknown, so a typo is visible before it is
 * submitted.
 */

/** What a run of characters is, for colouring purposes. */
export type DslTokenKind =
  | "whitespace"
  | "function"
  | "field"
  | "unknown"
  | "frequency"
  | "number"
  | "string"
  | "operator"
  | "punctuation";

export interface DslToken {
  kind: DslTokenKind;
  text: string;
  /** Character offset into the source, so an error position can be located. */
  start: number;
}

/** OHLCV shorthands. Always known, whatever the screen carries. */
export const FIELD_ALIASES: Readonly<Record<string, string>> = {
  o: "open",
  h: "high",
  l: "low",
  c: "close",
  v: "volume",
  vol: "volume",
};

/**
 * The blended prices by their charting names — `hlc3` is `typicalPrice(c)`.
 * The parser's `PRICE_ALIASES`; a call, so they highlight as one.
 */
export const PRICE_ALIASES: Readonly<Record<string, string>> = {
  hl2: "medianPrice",
  hlc3: "typicalPrice",
  ohlc4: "averagePrice",
  hlcc4: "weightedClose",
};

/** `index` is the bar number, synthesised rather than stored. */
const INDEX_FIELD = "index";

/** Units a frequency segment may use. `mi` is minute, `m` is month. */
const FREQUENCY_UNITS = new Set(["s", "mi", "h", "d", "w", "m", "q", "y"]);

const TOKEN_RE = new RegExp(
  [
    "(?<whitespace>\\s+)",
    "(?<string>\"(?:[^\"\\\\]|\\\\.)*\"?|'(?:[^'\\\\]|\\\\.)*'?)",
    // Leading digits allowed, and tried before `number` — see the module note.
    "(?<ident>\\d+[A-Za-z_]\\w*|[A-Za-z_]\\w*)",
    "(?<number>\\d+\\.\\d+|\\d+)",
    "(?<operator><=|>=|==|!=|<>|&&|\\|\\||[-+*/%<>=])",
    "(?<punctuation>[(),.\\[\\]])",
  ].join("|"),
  "gy",
);

/** Is this identifier a frequency, given what precedes it? */
function isFrequency(text: string, previousNonSpace: DslToken | undefined): boolean {
  // A frequency only ever appears in postfix position: `c.w`, `c.2w`.
  if (previousNonSpace?.text !== ".") return false;
  const unit = text.replace(/^\d+/, "");
  return unit !== text || FREQUENCY_UNITS.has(unit) ? FREQUENCY_UNITS.has(unit) : false;
}

/** Strip an inline first argument: `sma20` -> `sma`. Postfix sugar only. */
function withoutTrailingDigits(name: string): string {
  const match = /^([A-Za-z_]\w*?)(\d+)$/.exec(name);
  return match ? match[1]! : name;
}

export interface HighlightOptions {
  /** Function names, so a call is distinguishable from a column. */
  functions?: Iterable<string>;
  /** Columns this screen actually has, so a typo shows as unknown. */
  fields?: Iterable<string>;
}

/**
 * Split `source` into coloured runs. Never throws, and always covers every
 * character — the concatenated `text` equals the input exactly, which is what
 * lets the result be laid under a textarea and stay aligned.
 */
export function tokenizeDsl(source: string, options: HighlightOptions = {}): DslToken[] {
  const functions = new Set(options.functions ?? []);
  const fields = new Set(options.fields ?? []);
  const tokens: DslToken[] = [];
  let previous: DslToken | undefined;

  let index = 0;
  while (index < source.length) {
    TOKEN_RE.lastIndex = index;
    const match = TOKEN_RE.exec(source);
    if (!match || match.index !== index) {
      // A character the grammar has no rule for. Emitted rather than skipped so
      // the underlay stays character-for-character aligned with the textarea.
      tokens.push({ kind: "unknown", text: source[index]!, start: index });
      index += 1;
      continue;
    }

    const groups = match.groups!;
    const text = match[0];
    let kind: DslTokenKind;

    if (groups["whitespace"] !== undefined) kind = "whitespace";
    else if (groups["string"] !== undefined) kind = "string";
    else if (groups["number"] !== undefined) kind = "number";
    else if (groups["operator"] !== undefined) kind = "operator";
    else if (groups["punctuation"] !== undefined) kind = "punctuation";
    else {
      // An identifier: function, frequency, known field, or unknown.
      const ident = text;
      if (isFrequency(ident, previous)) kind = "frequency";
      else if (
        ident in PRICE_ALIASES ||
        functions.has(ident) ||
        functions.has(withoutTrailingDigits(ident))
      )
        kind = "function";
      else if (ident in FIELD_ALIASES || ident === INDEX_FIELD || fields.has(ident))
        kind = "field";
      // With no schema to check against, an identifier is assumed to be a
      // column: marking everything unknown would be noise, not information.
      else kind = fields.size === 0 ? "field" : "unknown";
    }

    const token: DslToken = { kind, text, start: index };
    tokens.push(token);
    if (kind !== "whitespace") previous = token;
    index += text.length;
  }

  return tokens;
}

/** CSS colour per token kind, as `var(--qf-*)` so a theme switch is free. */
export const TOKEN_COLORS: Readonly<Record<DslTokenKind, string>> = {
  whitespace: "inherit",
  function: "var(--qf-accent)",
  field: "var(--qf-text)",
  // Deliberately the negative colour: an identifier this screen does not have is
  // the single most common mistake, and it should look wrong before submitting.
  unknown: "var(--qf-negative)",
  frequency: "var(--qf-warn)",
  number: "var(--qf-positive)",
  string: "var(--qf-positive)",
  operator: "var(--qf-text-secondary)",
  punctuation: "var(--qf-text-muted)",
};
